import { useState, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import StarField from '../components/StarField';
import styles from './Gallery.module.css';

// 原型对应的颜色
const archetypeColors = {
  '叛逆者': '#9b59b6',
  '魔法师': '#3498db',
  '英雄': '#e74c3c',
  '情人': '#e91e63',
  '照顾者': '#27ae60',
  '弄臣': '#f39c12',
  '凡人': '#95a5a6',
  '创造者': '#1abc9c',
  '统治者': '#f1c40f',
  '智者': '#607d8b',
  '探索者': '#8e44ad',
  '天真者': '#00bcd4',
};

const getArchetypeColor = (archetype) => {
  if (!archetype) return '#9b59b6';
  for (const [key, color] of Object.entries(archetypeColors)) {
    if (archetype.includes(key)) return color;
  }
  return '#9b59b6';
};

// 生成星际坐标 - 基于ID生成固定坐标
const generateCoordinate = (id) => {
  const sectors = ['Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon', 'Zeta', 'Eta', 'Theta'];
  // 用ID的哈希值来确定坐标，保证同一个黑匣子坐标不变
  const hash = id ? id.toString().split('').reduce((a, b) => a + b.charCodeAt(0), 0) : Math.random() * 1000;
  const sector = sectors[hash % sectors.length];
  const num1 = ((hash * 7) % 999).toString().padStart(3, '0');
  const num2 = ((hash * 13) % 99).toString().padStart(2, '0');
  return `${sector}-${num1}.${num2}`;
};

// 根据创建时间计算"遗迹年代"（相对于现在过了多久）
const calculateAge = (dateStr) => {
  if (!dateStr) return '未知';
  
  // 解析日期字符串，格式如 "2024/12/27 15:30"
  const created = new Date(dateStr.replace(/\//g, '-'));
  const now = new Date();
  const diffMs = now - created;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return '刚刚发射';
  if (diffMins < 60) return `${diffMins}分钟前`;
  if (diffHours < 24) return `${diffHours}小时前`;
  if (diffDays < 7) return `${diffDays}天前`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}周前`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}个月前`;
  return `${Math.floor(diffDays / 365)}年前`;
};

// 根据共鸣次数计算"保存状态"
const calculateCondition = (echoCount) => {
  if (echoCount >= 10) return { text: '能量充沛', level: 5 };
  if (echoCount >= 5) return { text: '信号强劲', level: 4 };
  if (echoCount >= 3) return { text: '状态良好', level: 3 };
  if (echoCount >= 1) return { text: '微弱共鸣', level: 2 };
  return { text: '等待共鸣', level: 1 };
};

// 生成不重叠的随机位置
const generateSpreadPositions = (count) => {
  const positions = [];
  const minDistance = 15;
  const maxAttempts = 100;
  
  for (let i = 0; i < count; i++) {
    let bestPos = null;
    let bestMinDist = 0;
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const x = 8 + Math.random() * 84;
      const y = 8 + Math.random() * 76;
      
      let minDist = Infinity;
      for (const pos of positions) {
        const dist = Math.sqrt((x - pos.x) ** 2 + (y - pos.y) ** 2);
        minDist = Math.min(minDist, dist);
      }
      
      if (positions.length === 0 || minDist >= minDistance) {
        bestPos = { x, y };
        break;
      }
      
      if (minDist > bestMinDist) {
        bestMinDist = minDist;
        bestPos = { x, y };
      }
    }
    
    positions.push(bestPos || { x: 8 + Math.random() * 84, y: 8 + Math.random() * 76 });
  }
  
  return positions;
};

export default function Gallery() {
  const { dispatch, state } = useApp();
  const [paintings, setPaintings] = useState([]);
  const [selectedRelic, setSelectedRelic] = useState(null);
  const [excavationPhase, setExcavationPhase] = useState(0);
  const [brushPosition, setBrushPosition] = useState({ x: 0, y: 0 });
  const [dustCleared, setDustCleared] = useState(new Set());
  const [isExcavating, setIsExcavating] = useState(false);
  const [filterArchetype, setFilterArchetype] = useState('all'); // 原型筛选
  const excavationRef = useRef(null);

  // 获取当前用户的原型
  const userArchetype = state.scores ? 
    Object.entries(state.scores).sort((a, b) => b[1] - a[1])[0]?.[0] : null;

  useEffect(() => {
    const saved = localStorage.getItem('gallery_paintings');
    if (saved) {
      const data = JSON.parse(saved);
      
      const needsReposition = data.some((p, i) => {
        if (!p.posX || !p.posY) return true;
        for (let j = 0; j < i; j++) {
          const other = data[j];
          if (other.posX && other.posY) {
            const dist = Math.sqrt((p.posX - other.posX) ** 2 + (p.posY - other.posY) ** 2);
            if (dist < 12) return true;
          }
        }
        return false;
      });
      
      const spreadPositions = needsReposition ? generateSpreadPositions(data.length) : null;
      
      const enhanced = data.map((p, i) => {
        let coord = p.coordinate;
        if (typeof coord === 'object' && coord !== null || !coord) {
          coord = generateCoordinate(p.id);
        }
        const pos = spreadPositions ? spreadPositions[i] : null;
        const echoCount = p.echoCount || 0;
        
        return {
          ...p,
          coordinate: coord,
          // 不再存储 age 和 condition，每次动态计算
          echoCount: echoCount,
          posX: pos ? pos.x : (p.posX ?? (8 + Math.random() * 84)),
          posY: pos ? pos.y : (p.posY ?? (8 + Math.random() * 76)),
        };
      });
      
      if (spreadPositions) {
        localStorage.setItem('gallery_paintings', JSON.stringify(enhanced));
      }
      
      setPaintings(enhanced);
    }
  }, []);

  const handleBack = () => {
    dispatch({ type: 'SET_PAGE', payload: 'landing' });
  };

  const startExcavation = (painting) => {
    setSelectedRelic(painting);
    setExcavationPhase(1);
    setDustCleared(new Set());
  };

  const handleBrushMove = useCallback((e) => {
    if (!isExcavating || excavationPhase >= 4) return;
    
    const rect = excavationRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setBrushPosition({ x, y });
    
    const gridX = Math.floor(x / 10);
    const gridY = Math.floor(y / 10);
    const key = `${gridX}-${gridY}`;
    
    setDustCleared(prev => {
      const newSet = new Set(prev);
      newSet.add(key);
      
      const clearedCount = newSet.size;
      if (clearedCount >= 8 && excavationPhase === 1) {
        setExcavationPhase(2);
      } else if (clearedCount >= 18 && excavationPhase === 2) {
        setExcavationPhase(3);
      } else if (clearedCount >= 30 && excavationPhase === 3) {
        setExcavationPhase(4);
        setIsExcavating(false);
      }
      
      return newSet;
    });
  }, [isExcavating, excavationPhase]);

  const handleDelete = (id) => {
    const updated = paintings.filter(p => p.id !== id);
    setPaintings(updated);
    localStorage.setItem('gallery_paintings', JSON.stringify(updated));
    setSelectedRelic(null);
    setExcavationPhase(0);
  };

  const handleEcho = (id) => {
    const updated = paintings.map(p => {
      if (p.id === id) {
        return { ...p, echoCount: (p.echoCount || 0) + 1 };
      }
      return p;
    });
    setPaintings(updated);
    localStorage.setItem('gallery_paintings', JSON.stringify(updated));
    setSelectedRelic(updated.find(p => p.id === id));
  };

  const closeExcavation = () => {
    setSelectedRelic(null);
    setExcavationPhase(0);
    setDustCleared(new Set());
  };

  // 筛选作品
  const filteredPaintings = paintings.filter(p => {
    if (filterArchetype === 'all') return true;
    if (filterArchetype === 'similar' && userArchetype) {
      // 筛选相似原型的作品
      const archetypeMap = {
        'ruler': '统治者', 'hero': '英雄', 'sage': '智者', 'explorer': '探索者',
        'outlaw': '叛逆者', 'magician': '魔法师', 'lover': '情人', 'jester': '弄臣',
        'everyman': '凡人', 'caregiver': '照顾者', 'creator': '创造者', 'innocent': '天真者'
      };
      const userArchetypeName = archetypeMap[userArchetype];
      return p.archetype && p.archetype.includes(userArchetypeName);
    }
    return p.archetype && p.archetype.includes(filterArchetype);
  });

  // 获取所有原型列表
  const allArchetypes = [...new Set(paintings.map(p => {
    if (!p.archetype) return null;
    for (const key of Object.keys(archetypeColors)) {
      if (p.archetype.includes(key)) return key;
    }
    return null;
  }).filter(Boolean))];

  return (
    <div className={styles.container}>
      <StarField density={80} speed={0.1} />
      <div className={styles.gridOverlay} />
      
      {/* 背景装饰 - 漩涡 */}
      <img src="/images/vortex.png" alt="" className={styles.bgVortex} />
      
      {/* 背景装饰 - 星球 */}
      <img src="/images/saturn.png" alt="" className={styles.bgSaturn} />
      <img src="/images/small-planet.png" alt="" className={styles.bgSmallPlanet} />
      
      <button className={styles.backBtn} onClick={handleBack}>
        ← 返回
      </button>

      <div className={styles.header}>
        <h1 className={styles.title}>深空档案馆</h1>
        <p className={styles.subtitle}>点击黑匣子发掘遗迹</p>
        
        {/* 原型筛选 */}
        {paintings.length > 0 && (
          <div className={styles.filterBar}>
            <button 
              className={`${styles.filterBtn} ${filterArchetype === 'all' ? styles.filterActive : ''}`}
              onClick={() => setFilterArchetype('all')}
            >
              全部 ({paintings.length})
            </button>
            {userArchetype && (
              <button 
                className={`${styles.filterBtn} ${styles.filterSimilar} ${filterArchetype === 'similar' ? styles.filterActive : ''}`}
                onClick={() => setFilterArchetype('similar')}
              >
                ✦ 同类原型
              </button>
            )}
            {allArchetypes.slice(0, 5).map(arch => (
              <button 
                key={arch}
                className={`${styles.filterBtn} ${filterArchetype === arch ? styles.filterActive : ''}`}
                style={{ '--filter-color': archetypeColors[arch] }}
                onClick={() => setFilterArchetype(filterArchetype === arch ? 'all' : arch)}
              >
                {arch}
              </button>
            ))}
          </div>
        )}
      </div>

      {paintings.length === 0 ? (
        <div className={styles.empty}>
          {/* 空状态用 radio 图片 */}
          <img src="/images/radio.png" alt="" className={styles.emptyRadio} />
          <p className={styles.emptyText}>扫描中... 未检测到信号</p>
          <p className={styles.emptyHint}>完成测试并绘制自画像，你的黑匣子将被发射至深空</p>
        </div>
      ) : (
        <div className={styles.spaceField}>
          {filteredPaintings.length === 0 ? (
            <div className={styles.noResults}>
              <p>未找到匹配的黑匣子</p>
              <button className={styles.resetFilter} onClick={() => setFilterArchetype('all')}>
                显示全部
              </button>
            </div>
          ) : filteredPaintings.map((painting) => {
            const color = getArchetypeColor(painting.archetype);
            const echoCount = painting.echoCount || 0;
            // 根据共鸣次数计算亮度参数（柔和效果）
            const glowSize = 15 + Math.min(15, echoCount * 2); // 光晕大小 15-30px
            const glowOpacity = 0.1 + Math.min(0.25, echoCount * 0.03); // 光晕透明度 0.1-0.35
            const coreSize = 6 + Math.min(4, echoCount * 0.8); // 核心大小 6-10px
            const boxSize = 32 + Math.min(10, echoCount * 1.5); // 盒子大小 32-42px
            const shadowBlur = 8 + Math.min(12, echoCount * 2); // 阴影模糊 8-20px
            
            return (
              <div
                key={painting.id}
                className={styles.boxItem}
                style={{ 
                  left: `${painting.posX}%`, 
                  top: `${painting.posY}%`,
                  '--glow-color': color,
                  '--glow-size': `${glowSize}px`,
                  '--glow-opacity': glowOpacity,
                  '--core-size': `${coreSize}px`,
                  '--box-size': `${boxSize}px`,
                  '--shadow-blur': `${shadowBlur}px`,
                }}
                onClick={() => startExcavation(painting)}
              >
                <div className={styles.boxVisual}>
                  <div className={styles.boxFrame}>
                    <div className={styles.boxCore} />
                  </div>
                </div>
                <div className={styles.boxInfo}>
                  <span className={styles.boxCoord}>{painting.coordinate}</span>
                  {echoCount > 0 && (
                    <span className={styles.echoCount}>✧{echoCount}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 发掘界面 - 简洁版 */}
      {selectedRelic && (
        <div className={styles.excavationOverlay} onClick={closeExcavation}>
          {/* 背景装饰 */}
          <img src="/images/vortex.png" alt="" className={styles.modalBgVortex} />
          <img src="/images/mountains.png" alt="" className={styles.modalBgMountains} />
          
          <div className={styles.modalCard} onClick={e => e.stopPropagation()}>
            {/* 关闭按钮 */}
            <button className={styles.modalClose} onClick={closeExcavation}>×</button>
            
            {/* 顶部装饰线 */}
            <div className={styles.modalTopLine} />
            
            {/* 主内容 */}
            <div className={styles.modalContent}>
              {/* 左侧 - 图片 */}
              <div className={styles.modalLeft}>
                <div className={styles.imageFrame}>
                  <img 
                    src={selectedRelic.image} 
                    alt="" 
                    className={styles.modalImage}
                    draggable={false}
                  />
                  {/* 角落装饰 */}
                  <div className={styles.frameCorner} data-pos="tl" />
                  <div className={styles.frameCorner} data-pos="tr" />
                  <div className={styles.frameCorner} data-pos="bl" />
                  <div className={styles.frameCorner} data-pos="br" />
                </div>
                
                {/* 坐标标签 */}
                <div className={styles.coordTag}>
                  <span className={styles.coordIcon}>◈</span>
                  {selectedRelic.coordinate}
                </div>
              </div>
              
              {/* 右侧 - 信息 */}
              <div className={styles.modalRight}>
                {/* 装饰星球 */}
                <img src="/images/planets/saturn.png" alt="" className={styles.modalPlanetDecor} />
                
                {/* 原型标题 */}
                <div className={styles.archetypeTitle}>
                  <span className={styles.archetypeLabel}>人格印记</span>
                  <h2 className={styles.archetypeName}>{selectedRelic.archetype}</h2>
                </div>
                
                {/* 信息卡片 */}
                <div className={styles.infoCards}>
                  <div className={styles.infoCard}>
                    <span className={styles.infoCardLabel}>发射时间</span>
                    <span className={styles.infoCardValue}>{calculateAge(selectedRelic.date)}</span>
                  </div>
                  <div className={styles.infoCard}>
                    <span className={styles.infoCardLabel}>共鸣次数</span>
                    <span className={styles.infoCardValue}>{selectedRelic.echoCount || 0}</span>
                  </div>
                </div>
                
                {/* 漂流物语 */}
                {selectedRelic.message && (
                  <div className={styles.messageBox}>
                    <div className={styles.messageQuote}>"</div>
                    <p className={styles.messageText}>{selectedRelic.message}</p>
                  </div>
                )}
                
                {/* 操作按钮 */}
                <div className={styles.modalActions}>
                  <button 
                    className={styles.echoButton}
                    onClick={() => handleEcho(selectedRelic.id)}
                  >
                    <span>✧</span> 发送共鸣
                  </button>
                  <button 
                    className={styles.deleteButton}
                    onClick={() => handleDelete(selectedRelic.id)}
                  >
                    销毁
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.statusBar}>
        <span>已发现遗迹: {paintings.length}</span>
      </div>
    </div>
  );
}
